import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { PostCard } from '../components/blog/PostCard';
import { paginatePosts } from '../lib/blog';
import { useCurrentLocale, useLocalizedPath } from '../i18n/useLocalizedPath';

const SITE_URL = 'https://bolaoelite.com.br';

export default function Blog() {
  const { t } = useTranslation('blog');
  const locale = useCurrentLocale();
  const path = useLocalizedPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const pagination = useMemo(
    () => paginatePosts(pageParam, locale, 12),
    [pageParam, locale]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pagination.page]);

  const blogUrl = `${SITE_URL}${path('blog')}`;

  const blogListSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: t('list.schema_name', 'Blog do Bolão Elite'),
      description: t(
        'list.schema_desc',
        'Guias de campeonatos, estratégia de palpites e curiosidades sobre o futebol e o universo dos bolões.'
      ),
      url: blogUrl,
      inLanguage: locale,
      publisher: {
        '@type': 'Organization',
        name: 'Bolão Elite',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo-512.png`,
        },
      },
      blogPost: pagination.posts.map(p => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: `${blogUrl}/${p.slug}`,
        datePublished: p.publishedAt,
        author: { '@type': 'Person', name: p.author },
        image: `${SITE_URL}${p.coverImage.startsWith('/') ? p.coverImage : '/' + p.coverImage}`,
        articleSection: t(`category.${p.category}`, p.category),
      })),
    }),
    [pagination.posts, blogUrl, locale, t]
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('list.seo_title', 'Blog do Bolão Elite — Guias, estratégia e curiosidades de futebol')}
        description={t(
          'list.seo_desc',
          'Aprenda mais sobre os principais campeonatos, melhore sua estratégia de palpites e conheça curiosidades do futebol no blog oficial do Bolão Elite.'
        )}
        canonical={blogUrl}
        ogUrl={blogUrl}
        keywords={t('list.seo_keywords', 'blog bolão, guia campeonatos, estratégia palpites, curiosidades futebol')}
        jsonLd={blogListSchema}
      />

      <PublicHeader />

      <header className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to={path('home')} className="hover:text-white transition-colors">
              {t('breadcrumb.home', 'Início')}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('breadcrumb.blog', 'Blog')}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            {t('list.h1', 'Blog do Bolão Elite')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">
            {t(
              'list.intro',
              'Guias de campeonatos, estratégia de palpites e histórias do futebol — para você jogar bolão com mais conhecimento e se divertir com os amigos.'
            )}
          </p>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-12">
        {pagination.posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-base">
              {t('list.empty', 'Estamos preparando os primeiros artigos. Volte em breve!')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pagination.posts.map(p => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <nav className="mt-12 flex items-center justify-center gap-2" aria-label="paginação">
                <button
                  type="button"
                  disabled={!pagination.hasPrev}
                  onClick={() => setSearchParams({ page: String(pagination.page - 1) })}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t('list.prev', '← Anterior')}
                </button>
                <span className="text-sm text-gray-500 px-3">
                  {t('list.page_of', 'Página {{page}} de {{total}}', {
                    page: pagination.page,
                    total: pagination.totalPages,
                  })}
                </span>
                <button
                  type="button"
                  disabled={!pagination.hasNext}
                  onClick={() => setSearchParams({ page: String(pagination.page + 1) })}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t('list.next', 'Próxima →')}
                </button>
              </nav>
            )}
          </>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}
